import { Check, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { Button, Input, Select, Spinner, cn } from '@buildbee/ui';
import { trpc } from '../../../lib/trpc';

type Option = { id: string; label: string; hint?: string | null };

function useDebounced<T>(value: T, ms = 200): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Accessible search-as-you-type picker (combobox pattern). */
function SearchPicker({
  value,
  valueLabel,
  onChange,
  options,
  loading,
  query,
  onQuery,
  placeholder,
  id,
  disabled,
}: {
  value: string | null | undefined;
  valueLabel?: string | null;
  onChange: (id: string | null, option?: Option) => void;
  options: Option[];
  loading?: boolean;
  query: string;
  onQuery: (q: string) => void;
  placeholder: string;
  id?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const pick = (o: Option) => {
    onChange(o.id, o);
    onQuery('');
    setOpen(false);
  };
  if (value && !open) {
    return (
      <div className="flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm">
        <span className="min-w-0 flex-1 truncate">{valueLabel ?? 'Selected'}</span>
        {!disabled ? (
          <button type="button" aria-label="Clear" className="text-muted-foreground hover:text-foreground" onClick={() => onChange(null)}>
            <X className="size-4" />
          </button>
        ) : null}
      </div>
    );
  }
  return (
    <div ref={ref} className="relative">
      <Input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        disabled={disabled}
        placeholder={placeholder}
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onQuery(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, options.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter' && open && options[active]) {
            e.preventDefault();
            pick(options[active]!);
          } else if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open ? (
        <ul id={listId} role="listbox" className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg bg-popover p-1 text-popover-foreground shadow-pop">
          {loading ? (
            <li className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
              <Spinner className="[&_svg]:size-4" /> Searching…
            </li>
          ) : options.length === 0 ? (
            <li className="px-2 py-1.5 text-sm text-muted-foreground">No matches</li>
          ) : (
            options.map((o, i) => (
              <li
                key={o.id}
                role="option"
                aria-selected={o.id === value}
                className={cn('flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm', i === active && 'bg-muted')}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o);
                }}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{o.label}</span>
                  {o.hint ? <span className="block truncate text-xs text-muted-foreground">{o.hint}</span> : null}
                </span>
                {o.id === value ? <Check className="size-4 text-primary" /> : null}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

export function ContactPicker(props: { value: string | null | undefined; valueLabel?: string | null; onChange: (id: string | null, label?: string, companyId?: string | null) => void; id?: string; disabled?: boolean }) {
  const [q, setQ] = useState('');
  const debounced = useDebounced(q);
  const res = trpc.crm.contacts.search.useQuery({ q: debounced, limit: 15 });
  const selected = trpc.crm.contacts.get.useQuery({ id: props.value! }, { enabled: !!props.value && !props.valueLabel });
  const options = (res.data ?? []).map((c) => ({ id: c.id, label: c.displayName, hint: [c.primaryEmail, c.companyName].filter(Boolean).join(' · ') || null, companyId: c.companyId }));
  return (
    <SearchPicker
      id={props.id}
      disabled={props.disabled}
      value={props.value}
      valueLabel={props.valueLabel ?? selected.data?.displayName}
      onChange={(id, o) => props.onChange(id, o?.label, (o as (typeof options)[number] | undefined)?.companyId ?? null)}
      options={options}
      loading={res.isFetching}
      query={q}
      onQuery={setQ}
      placeholder="Search contacts…"
    />
  );
}

export function CompanyPicker(props: { value: string | null | undefined; valueLabel?: string | null; onChange: (id: string | null, label?: string) => void; id?: string; relationshipType?: string; disabled?: boolean }) {
  const [q, setQ] = useState('');
  const debounced = useDebounced(q);
  const res = trpc.crm.companies.search.useQuery({ q: debounced, limit: 15, relationshipType: props.relationshipType });
  const selected = trpc.crm.companies.get.useQuery({ id: props.value! }, { enabled: !!props.value && !props.valueLabel });
  return (
    <SearchPicker
      id={props.id}
      disabled={props.disabled}
      value={props.value}
      valueLabel={props.valueLabel ?? selected.data?.name}
      onChange={(id, o) => props.onChange(id, o?.label)}
      options={(res.data ?? []).map((c) => ({ id: c.id, label: c.name, hint: c.domain }))}
      loading={res.isFetching}
      query={q}
      onQuery={setQ}
      placeholder="Search companies…"
    />
  );
}

/** Workspace members (owner/assignee pickers). */
export function useMembers() {
  const members = trpc.workspace.members.list.useQuery({ includeSuspended: false }, { staleTime: 5 * 60_000 });
  return {
    ...members,
    options: (members.data ?? []).map((m) => ({ value: m.userId, label: m.name })),
    nameOf: (id: string | null | undefined) => (id ? (members.data?.find((m) => m.userId === id)?.name ?? null) : null),
  };
}

export function MemberSelect({
  value,
  onChange,
  id,
  placeholder = 'Unassigned',
  allowEmpty = true,
}: {
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  id?: string;
  placeholder?: string;
  allowEmpty?: boolean;
}) {
  const members = useMembers();
  return (
    <Select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} disabled={members.isLoading}>
      {allowEmpty ? <option value="">{placeholder}</option> : null}
      {members.options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  );
}

/** Small "x of y" pager for cursor lists. */
export function LoadMore({ hasMore, loading, onClick }: { hasMore: boolean; loading: boolean; onClick: () => void }) {
  if (!hasMore) return null;
  return (
    <div className="flex justify-center pt-3">
      <Button variant="outline" size="sm" loading={loading} onClick={onClick}>
        Load more
      </Button>
    </div>
  );
}
